import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { docusignApi, extractErrorMessage } from '../../../services/hrApi';
import type { WizardStepKey } from '../types';
import './StepStyles.scss';

interface SignatureVerificationStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
  onNavigateToStep?: (step: WizardStepKey) => void;
  onUpdateStepData?: (step: WizardStepKey, data: any) => void;
}

const SignatureVerificationStep: React.FC<SignatureVerificationStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
  onNavigateToStep,
  onUpdateStepData,
}) => {
  const [checkingStatus, setCheckingStatus] = useState(true); // Start with loading state
  const [envelopeStatus, setEnvelopeStatus] = useState<any>(null);
  const [processingAction, setProcessingAction] = useState(false);

  // Automatically check envelope signature status when component loads or employeeId changes
  useEffect(() => {
    if (employeeId) {
      checkSignatureStatus();
    } else {
      setCheckingStatus(false);
    }
  }, [employeeId]);

  const checkSignatureStatus = async (isManualRefresh: boolean = false) => {
    if (!employeeId) {
      setCheckingStatus(false);
      return;
    }
    
    setCheckingStatus(true);
    try {
      const result = await docusignApi.checkSignatureStatus(employeeId);
      
      if (result.success) {
        setEnvelopeStatus(result);
        // Show success message only if user manually clicked "Check Again"
        if (isManualRefresh) {
          toast.success('Signature status refreshed successfully');
        }
      } else {
        console.error('Failed to check signature status:', result.error);
        toast.error(`Failed to check signature status: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error checking signature status:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to check signature status: ${errorMessage}`);
    } finally {
      setCheckingStatus(false);
    }
  };

  // Get display status for envelope
  const getDisplayStatus = (status: string): string => {
    if (status === 'voided') {
      return 'Voided';
    } else if (status === 'sent') {
      return 'Pending';
    } else if (status === 'completed') {
      return 'Completed';
    } else {
      // created, tabs_placed, or anything else - all are draft
      return 'In Draft';
    }
  };

  // Check if status is draft (created or tabs_placed)
  const isDraftStatus = (status: string): boolean => {
    return status === 'created' || status === 'tabs_placed';
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    if (status === 'voided') {
      return '#dc3545'; // red
    } else if (status === 'sent') {
      return '#ffc107'; // yellow/orange for pending
    } else if (status === 'completed') {
      return '#28a745'; // green
    } else {
      return '#6c757d'; // gray for draft
    }
  };

  // Get status configuration for UI
  const getStatusConfig = (status: string) => {
    if (status === 'completed') {
      return {
        icon: 'bx bx-check-circle',
        iconBackground: '#e8f5e9',
        iconShadow: 'rgba(40, 167, 69, 0.2)',
        borderColor: '#c3e6cb',
        badgeBackground: '#28a745',
        badgeColor: '#ffffff',
        badgeShadow: 'rgba(40, 167, 69, 0.3)',
        badgeIcon: 'bx bx-check',
        gradientColor: '#34ce57',
        description: 'All documents have been signed and completed successfully.'
      };
    } else if (status === 'sent') {
      return {
        icon: 'bx bx-time-five',
        iconBackground: '#fff3cd',
        iconShadow: 'rgba(255, 193, 7, 0.2)',
        borderColor: '#ffeaa7',
        badgeBackground: '#ffc107',
        badgeColor: '#212529',
        badgeShadow: 'rgba(255, 193, 7, 0.3)',
        badgeIcon: 'bx bx-hourglass',
        gradientColor: '#ffd54f',
        description: 'Documents have been sent and are awaiting signature from the recipient.'
      };
    } else if (status === 'voided') {
      return {
        icon: 'bx bx-x-circle',
        iconBackground: '#f8d7da',
        iconShadow: 'rgba(220, 53, 69, 0.2)',
        borderColor: '#f5c6cb',
        badgeBackground: '#dc3545',
        badgeColor: '#ffffff',
        badgeShadow: 'rgba(220, 53, 69, 0.3)',
        badgeIcon: 'bx bx-x',
        gradientColor: '#e4606d',
        description: 'This envelope has been voided. You may need to create a new one.'
      };
    } else {
      // created, tabs_placed, or anything else - all are draft
      return {
        icon: 'bx bx-file-blank',
        iconBackground: '#e9ecef',
        iconShadow: 'rgba(108, 117, 125, 0.2)',
        borderColor: '#dee2e6',
        badgeBackground: '#6c757d',
        badgeColor: '#ffffff',
        badgeShadow: 'rgba(108, 117, 125, 0.3)',
        badgeIcon: 'bx bx-edit',
        gradientColor: '#868e96',
        description: 'Envelope is in draft status. Go to Document Package step to place signature tabs and send.'
      };
    }
  };

  // Determine if step is complete (all envelopes are completed)
  useEffect(() => {
    if (envelopeStatus && envelopeStatus.has_envelope && envelopeStatus.envelopes) {
      const nonVoidedEnvelopes = envelopeStatus.envelopes.filter((env: any) => env.status !== 'voided');
      const allCompleted = nonVoidedEnvelopes.length > 0 && nonVoidedEnvelopes.every((env: any) => env.status === 'completed');
      onComplete(allCompleted);
    } else {
      onComplete(false);
    }
  }, [envelopeStatus, onComplete]);

  // Get the first non-voided envelope for actions
  const getActiveEnvelope = () => {
    if (!envelopeStatus || !envelopeStatus.has_envelope || !envelopeStatus.envelopes) {
      return null;
    }
    return envelopeStatus.envelopes.find((env: any) => env.status !== 'voided') || null;
  };

  const handleResendEnvelope = async () => {
    const activeEnvelope = getActiveEnvelope();
    if (!activeEnvelope || !activeEnvelope.envelope_id) {
      toast.error('No envelope found.');
      return;
    }

    if (activeEnvelope.status !== 'sent') {
      toast.error('Can only resend envelopes that have been sent.');
      return;
    }

    if (!window.confirm('Are you sure you want to resend this envelope? All pending recipients will receive a new notification email.')) {
      return;
    }

    setProcessingAction(true);
    try {
      const result = await docusignApi.resendEnvelope(activeEnvelope.envelope_id);

      if (result.success) {
        toast.success('Envelope resent successfully to all pending recipients.');
        // Refresh envelope status
        await checkSignatureStatus();
      } else {
        toast.error(`Failed to resend envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error resending envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to resend envelope: ${errorMessage}`);
    } finally {
      setProcessingAction(false);
    }
  };

  const handleVoidEnvelope = async () => {
    const activeEnvelope = getActiveEnvelope();
    if (!activeEnvelope || !activeEnvelope.envelope_id) {
      toast.error('No envelope found.');
      return;
    }

    if (activeEnvelope.status === 'voided') {
      toast.error('Envelope is already voided.');
      return;
    }

    if (!window.confirm('Are you sure you want to void this envelope? This action cannot be undone. You will be taken back to the Document Package step to create a new envelope.')) {
      return;
    }

    setProcessingAction(true);
    try {
      const result = await docusignApi.voidEnvelope(activeEnvelope.envelope_id);

      if (result.success) {
        // Reset step completion
        onComplete(false);
        // Reset package_sent in document_package step data
        if (onUpdateStepData) {
          onUpdateStepData('document_package', {
            package_sent: false,
            sent_date: '',
          });
        }
        // Refresh envelope status
        await checkSignatureStatus();
        // Navigate back to Document Package step immediately
        if (onNavigateToStep) {
          onNavigateToStep('document_package');
        } else {
          toast.success('Envelope voided successfully. Please go back to the Document Package step to create a new envelope.');
        }
      } else {
        toast.error(`Failed to void envelope: ${result.error || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error voiding envelope:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to void envelope: ${errorMessage}`);
    } finally {
      setProcessingAction(false);
    }
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Signature Verification</h4>
        <p>Track signed documents and verify signatures</p>
        {/* Check Again Button */}
        {!checkingStatus && employeeId && (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => checkSignatureStatus(true)}
              disabled={checkingStatus || processingAction}
              style={{
                padding: '8px 16px',
                background: '#0dcaf0',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: (checkingStatus || processingAction) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                opacity: (checkingStatus || processingAction) ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!checkingStatus && !processingAction) {
                  e.currentTarget.style.background = '#0aa2c0';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#0dcaf0';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {checkingStatus ? (
                <>
                  <i className="bx bx-loader-alt bx-spin"></i>
                  Checking...
                </>
              ) : (
                <>
                  <i className="bx bx-refresh"></i>
                  Check Again
                </>
              )}
            </button>
          </div>
        )}
      </div>

      <div className="step-form">
        {/* Loading State */}
        {checkingStatus && (
          <div style={{ 
            marginBottom: '20px', 
            textAlign: 'center', 
            padding: '60px 40px',
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            borderRadius: '12px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #0dcaf0 0%, #0aa2c0 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(13, 202, 240, 0.3)'
            }}>
              <i className="bx bx-loader-alt bx-spin" style={{ fontSize: '32px', color: '#ffffff' }}></i>
            </div>
            <p style={{ margin: 0, fontSize: '16px', color: '#495057', fontWeight: 500 }}>
              Checking envelope status...
            </p>
            <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#6c757d' }}>
              Please wait while we verify signature status
            </p>
          </div>
        )}

        {/* Envelope Status Display - Show all non-voided envelopes (including drafts) */}
        {!checkingStatus && envelopeStatus && envelopeStatus.has_envelope && envelopeStatus.envelopes && (() => {
          const nonVoidedEnvelopes = envelopeStatus.envelopes.filter((env: any) => env.status !== 'voided');
          
          // If no non-voided envelopes, show message
          if (nonVoidedEnvelopes.length === 0) {
            return (
              <div style={{
                marginBottom: '20px',
                padding: '48px 32px',
                textAlign: 'center'
              }}>
                <div style={{
                  width: '96px',
                  height: '96px',
                  margin: '0 auto 24px',
                  borderRadius: '50%',
                  background: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(108, 117, 125, 0.2)'
                }}>
                  <i className="bx bx-envelope" style={{ fontSize: '48px', color: '#6c757d' }}></i>
                </div>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#495057',
                  letterSpacing: '0.5px'
                }}>
                  No Active Envelopes
                </h3>
                <p style={{
                  margin: '0 auto',
                  fontSize: '14px',
                  color: '#6c757d',
                  lineHeight: '1.6',
                  maxWidth: '400px'
                }}>
                  Create an envelope in the Document Package step to see signature status here.
                </p>
              </div>
            );
          }
          
          // Show all non-voided envelopes (including drafts, sent, and completed)
          return (
            <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {nonVoidedEnvelopes.map((env: any, index: number) => {
                const displayStatus = getDisplayStatus(env.status);
                const statusColor = getStatusColor(env.status);
                const statusConfig = getStatusConfig(env.status);
                
                return (
                  <div
                    key={index}
                    style={{
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '32px 0'
                    }}
                  >
                    {/* Big Icon */}
                    <div
                      style={{
                        width: '96px',
                        height: '96px',
                        borderRadius: '50%',
                        background: statusConfig.iconBackground,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '24px',
                        boxShadow: `0 8px 24px ${statusConfig.iconShadow}`
                      }}
                    >
                      <i 
                        className={statusConfig.icon} 
                        style={{ 
                          fontSize: '48px', 
                          color: statusColor 
                        }}
                      ></i>
                    </div>
                    
                    {/* Status Text */}
                    <h3 style={{
                      margin: '0 0 12px 0',
                      fontSize: '20px',
                      fontWeight: 600,
                      color: statusColor,
                      letterSpacing: '0.5px'
                    }}>
                      {displayStatus}
                    </h3>
                    
                    {/* Status Message */}
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#6c757d',
                      lineHeight: '1.6',
                      maxWidth: '400px'
                    }}>
                      {statusConfig.description}
                    </p>
                    
                    {/* Completion Date (only for completed) */}
                    {env.status === 'completed' && env.completed_date && (
                      <p style={{
                        margin: '16px 0 0 0',
                        fontSize: '12px',
                        color: '#868e96',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}>
                        <i className="bx bx-calendar" style={{ fontSize: '14px' }}></i>
                        {new Date(env.completed_date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}

                    {/* Action Buttons - Show for sent status only (completed envelopes cannot be voided) */}
                    {env.status === 'sent' && (
                      <div style={{
                        marginTop: '32px',
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          type="button"
                          onClick={handleResendEnvelope}
                          disabled={processingAction}
                          style={{
                            padding: '10px 20px',
                            background: '#0dcaf0',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: processingAction ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            opacity: processingAction ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!processingAction) {
                              e.currentTarget.style.background = '#0aa2c0';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#0dcaf0';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {processingAction ? (
                            <>
                              <i className="bx bx-loader-alt bx-spin"></i>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-revision"></i>
                              Resend
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={handleVoidEnvelope}
                          disabled={processingAction}
                          style={{
                            padding: '10px 20px',
                            background: '#dc3545',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: 500,
                            cursor: processingAction ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease',
                            opacity: processingAction ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (!processingAction) {
                              e.currentTarget.style.background = '#c82333';
                              e.currentTarget.style.transform = 'translateY(-1px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#dc3545';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          {processingAction ? (
                            <>
                              <i className="bx bx-loader-alt bx-spin"></i>
                              Processing...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-x-circle"></i>
                              Void
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* No Envelopes Message */}
        {!checkingStatus && envelopeStatus && !envelopeStatus.has_envelope && (
          <div style={{
            marginBottom: '20px',
            padding: '32px',
            background: 'linear-gradient(135deg, #e3f2fd 0%, #ffffff 100%)',
            borderRadius: '12px',
            border: '1px solid #90caf9',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: '#e3f2fd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="bx bx-info-circle" style={{ fontSize: '32px', color: '#2196f3' }}></i>
            </div>
            <h5 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: '#1976d2' }}>
              No Envelopes Found
            </h5>
            <p style={{ margin: 0, fontSize: '14px', color: '#546e7a', lineHeight: '1.6' }}>
              No envelopes found for this employee. Please complete the Document Package step first to create and send documents for signature.
            </p>
          </div>
        )}

        {/* Error State */}
        {!checkingStatus && !envelopeStatus && employeeId && (
          <div style={{
            marginBottom: '20px',
            padding: '32px',
            background: 'linear-gradient(135deg, #ffebee 0%, #ffffff 100%)',
            borderRadius: '12px',
            border: '1px solid #ef9a9a',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 16px',
              borderRadius: '50%',
              background: '#ffebee',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <i className="bx bx-error-circle" style={{ fontSize: '32px', color: '#d32f2f' }}></i>
            </div>
            <h5 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: 600, color: '#c62828' }}>
              Unable to Load Status
            </h5>
            <p style={{ margin: 0, fontSize: '14px', color: '#546e7a', lineHeight: '1.6' }}>
              Unable to load envelope status. Please try refreshing the page or contact support if the issue persists.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignatureVerificationStep;
